/**
 * Local Media Database using IndexedDB (via Dexie)
 * Stores full-quality videos and proxies for offline/local-first workflow
 */

import Dexie, { Table } from 'dexie';
import { LocalShot, LocalMediaAsset, ImageAsset, ProjectState } from '../types/project';

// Database schema
class LocalMediaDatabase extends Dexie {
    shots!: Table<LocalShot, string>;
    assets!: Table<LocalMediaAsset, string>;
    characters!: Table<LocalCharacter, string>;
    folders!: Table<LocalFolder, string>;
    imageAssets!: Table<ImageAsset, string>;
    projectStates!: Table<ProjectState & { projectId: string }, string>;

    constructor() {
        super('StudioJenialDB');

        this.version(1).stores({
            shots: 'id, projectId, createdAt',
            assets: 'id, projectId, filename, createdAt',
        });

        // Version 2: Add characters table
        this.version(2).stores({
            shots: 'id, projectId, createdAt',
            assets: 'id, projectId, filename, createdAt',
            characters: 'id, name, createdAt'
        });

        // Version 3: Add folders table
        this.version(3).stores({
            shots: 'id, projectId, createdAt',
            assets: 'id, projectId, filename, createdAt',
            characters: 'id, name, createdAt',
            folders: 'id, type, parentId, name, createdAt'
        });

        // Version 4: Add imageAssets table for P0.1 (Preview vs Asset persistence)
        this.version(4).stores({
            shots: 'id, projectId, createdAt',
            assets: 'id, projectId, filename, createdAt',
            characters: 'id, name, createdAt',
            folders: 'id, type, parentId, name, createdAt',
            imageAssets: 'id, projectId, segmentIndex, type, shotId, createdAt, [projectId+segmentIndex+type]',
            projectStates: 'projectId, savedAt'
        });
    }
}

const db = new LocalMediaDatabase();

// === SHOTS API ===

export async function saveShot(shot: LocalShot): Promise<void> {
    await db.shots.put(shot);
    console.log(`[LocalMediaDB] Saved shot: ${shot.id}`);
}

export async function getShot(id: string): Promise<LocalShot | undefined> {
    return db.shots.get(id);
}

export async function getShotsByProject(projectId: string): Promise<LocalShot[]> {
    return db.shots.where('projectId').equals(projectId).toArray();
}

export async function deleteShot(id: string): Promise<void> {
    await db.shots.delete(id);
}

// === ASSETS API ===

export async function saveAsset(asset: LocalMediaAsset): Promise<void> {
    await db.assets.put(asset);
    console.log(`[LocalMediaDB] Saved asset: ${asset.filename}`);
}

export async function getAsset(id: string): Promise<LocalMediaAsset | undefined> {
    return db.assets.get(id);
}

export async function getAssetsByProject(projectId: string): Promise<LocalMediaAsset[]> {
    return db.assets.where('projectId').equals(projectId).toArray();
}

export async function deleteAsset(id: string): Promise<void> {
    await db.assets.delete(id);
}

// === CHARACTERS API (v2) ===

import { LocalCharacter, LocalFolder } from '../types/project';

export async function saveCharacter(character: LocalCharacter): Promise<void> {
    await db.characters.put(character);
    console.log(`[LocalMediaDB] Saved character: ${character.name}`);
}

export async function getCharacter(id: string): Promise<LocalCharacter | undefined> {
    return db.characters.get(id);
}

export async function getAllCharacters(): Promise<LocalCharacter[]> {
    // Characters are global, not per-project
    return db.characters.toArray();
}

export async function deleteCharacter(id: string): Promise<void> {
    await db.characters.delete(id);
}

// === FOLDERS API (v3) ===

export async function saveFolder(folder: LocalFolder): Promise<void> {
    await db.folders.put(folder);
    console.log(`[LocalMediaDB] Saved folder: ${folder.name}`);
}

export async function getFoldersByType(type: 'character' | 'dogma'): Promise<LocalFolder[]> {
    return db.folders.where('type').equals(type).toArray();
}

export async function deleteFolder(id: string): Promise<void> {
    await db.folders.delete(id);
    // Note: We don't cascade delete contents automatically here to be safe.
    // The consumer should handle moving/deleting children.
}


// === UTILITY ===

export async function clearProjectData(projectId: string): Promise<void> {
    await db.shots.where('projectId').equals(projectId).delete();
    await db.assets.where('projectId').equals(projectId).delete();
    // Do not delete global characters or folders
}

export async function getDatabaseSize(): Promise<{ shots: number; assets: number }> {
    const shotsCount = await db.shots.count();
    const assetsCount = await db.assets.count();
    return { shots: shotsCount, assets: assetsCount };
}

// === BLOB HELPERS ===

export function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export function base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
}

export { db };
